import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';

// Service options type
export type TOptions = {
  prefix?: string;
  defaultState?: object;
};

// Store state type
export type TState = {
  [index: string]: any;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class StorageService extends AbstractService<TOptions, TState> {
  private readonly _prefix: string;

  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'storage');
    this._prefix = options.prefix || 'storage';
    this.defaultState = options.defaultState || {};
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<TState>) {
    await super.init(Vue, store);

    // Configure store
    this.store.registerModule(['pdc', this._prefix], {
      namespaced: true,
      state: this.getDefaultState(),
      mutations: {
        SET(state: TState, { key, value }: { key: string; value: any }) {
          Vue.set(state, key, value);
        },
      },
    });
  }

  getDefaultState(): TState {
    return this.defaultState;
  }

  // ---------------------------------------------------------------------------
  // SHORTCUTS

  /**
   * Set universal
   *
   * @since 1.0.0
   */
  public setUniversal(key: string, value: any) {
    // Local state
    this.setState(key, value);

    // Local storage
    this.setLocalStorage(key, value);

    return value;
  }

  /**
   * Get universal
   *
   * @since 1.0.0
   */
  public getUniversal(key: string) {
    // Local state
    let value = this.getState(key);

    // Local Storage
    if (StorageService.isUnset(value)) {
      value = this.getLocalStorage(key);
    }

    return value;
  }

  /**
   * Sync universal
   *
   * @since 1.0.0
   */
  syncUniversal(key: string, defaultValue: any) {
    let value = this.getUniversal(key);

    if (StorageService.isUnset(value) && StorageService.isSet(defaultValue)) {
      value = defaultValue;
    }

    if (StorageService.isSet(value)) {
      this.setUniversal(key, value);
    }

    return value;
  }

  /**
   * Remove universal
   *
   * @since 1.0.0
   */
  public removeUniversal(key: string) {
    this.removeState(key);
    this.removeLocalStorage(key);
  }

  /**
   * Remove universal
   *
   * @since 1.0.0
   */
  private setState(key: string, value: any) {
    this.store.commit(`pdc/${this._prefix}/SET`, {
      key,
      value,
    });

    return value;
  }

  /**
   * Get state
   *
   * @since 1.0.0
   */
  private getState(key: string) {
    return this.store.state.pdc[this._prefix][key];
  }

  /**
   * Remove state
   *
   * @since 1.0.0
   */
  private removeState(key: string) {
    this.setState(key, undefined);
  }

  private setLocalStorage(key: string, value: any) {
    const _key = this.keyPrefixer(key);

    if (StorageService.isObjectOrArray(value)) {
      localStorage.setItem(_key, JSON.stringify(value));
    } else {
      localStorage.setItem(_key, value);
    }

    return value;
  }

  private getLocalStorage(key: string) {
    const _key = this.keyPrefixer(key);

    const value = localStorage.getItem(_key);

    return StorageService.decodeValue(value);
  }

  private removeLocalStorage(key: string) {
    const _key = this.keyPrefixer(key);

    localStorage.removeItem(_key);
  }

  // ---------------------------------------------------------------------------

  private keyPrefixer(key: string) {
    return `${this._prefix} > ${key}`;
  }

  private static isUnset(o: any) {
    return typeof o === 'undefined' || o === null;
  }

  private static isSet(o: any) {
    return !StorageService.isUnset(o);
  }

  private static isObjectOrArray(obj: any) {
    const type = Object.prototype.toString.call(obj);
    return type === '[object Object]' || type === '[object Array]';
  }

  private static decodeValue(val: any) {
    // Find current value's type
    const type = Object.prototype.toString.call(val);

    // If value is an object, return object
    if (type === '[object Object]') {
      return val;
    }

    // If value is somehow undefined, return as is (erroneous code)
    if (type === 'undefined') {
      return val;
    }

    // Finally try to parse it as json, or fallback to original value
    try {
      return JSON.parse(val);
    } catch (_) {
      return val;
    }
  }
}
